/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { Box, Chip as MuiChip, Typography, styled } from '@mui/material';
import React from 'react';
import MaterialSymbol from '../MaterialSymbol';
import ChipMenuDialog from '../ChipMenuDialog/ChipMenuDialog';

const LabelWrapper = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'space-between',
});

interface Props {
  label: string;
  value?: string;
  dropdown?: React.ReactNode[] | React.ReactNode;
  open: boolean;
  setOpen: (open: boolean) => void;
  testId: string;
}

const ChipMenu: React.FC<Props> = ({ value, label, dropdown, open, setOpen, testId }) => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const arrowRef = React.useRef<HTMLSpanElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setOpen(!open);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setOpen(false);
  };

  return (
    <>
      <MuiChip
        data-testid={testId}
        id={testId}
        variant='outlined'
        component='button'
        color='primary'
        onClick={handleClick}
        sx={{ height: '36px', borderRadius: '32px', padding: '3px' }}
        label={
          <LabelWrapper>
            <Typography variant='body2' fontWeight='bold' component='span' color='inherit'>
              {label}
              {value && ': '}
              {value && (
                <Typography variant='body2' component='span' color='inherit'>
                  {value}
                </Typography>
              )}
            </Typography>

            <Box display='flex' position='relative' ref={arrowRef}>
              <MaterialSymbol sx={{ ml: 1, mr: -1 }}>expand_more</MaterialSymbol>
            </Box>
          </LabelWrapper>
        }
      />

      <ChipMenuDialog open={open} onClose={handleClose} anchorEl={anchorEl}>
        {dropdown}
      </ChipMenuDialog>
    </>
  );
};

export default ChipMenu;
